import { useCallback } from 'react';

/**
 * Network calls used by NestedDocumentTable and DocumentSpreadsheetBody to resolve
 * items, negotiated partner discounts and stock totals for document/plan line rows.
 * Pure fetch + response-parsing extraction (CLAUDE.md rule 9) — no state logic moved,
 * callers keep their own caches (discountCacheRef, itemMetaById, localRows updates).
 */
export const useDocumentLineLookups = () => {
  // Bulk-resolve items by _id (used to preload the item cache before rows render).
  // Preserves the previous no-token behaviour: without a token the caller never fetched,
  // so this resolves to [] without hitting the network.
  const fetchItemsByIds = useCallback(async (ids: string[]): Promise<any[]> => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    const apiUrl = process.env.REACT_APP_API_URL ?? '';

    const response = await fetch(`${apiUrl}/api/items/bulk-ids`, {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ids })
    });

    if (!response.ok) return [];
    const data = await response.json();
    return data.items && data.items.length > 0 ? data.items : [];
  }, []);

  // Bulk-resolve items by ident (used by Excel import to map rows to items in one call).
  // Same no-token rule as fetchItemsByIds.
  const lookupItemsByIdents = useCallback(async (idents: string[]): Promise<any[]> => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    const apiUrl = process.env.REACT_APP_API_URL ?? '';

    const response = await fetch(`${apiUrl}/api/items/bulk-lookup`, {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ idents })
    });

    if (!response.ok) return [];
    const data = await response.json();
    return data.items ? data.items : [];
  }, []);

  // Negotiated price/discounts for a partner+item pair as of a given date.
  // Returns null on a non-ok response so callers can keep their existing "return early" guard.
  const fetchPartnerItemDiscount = useCallback(async (
    partnerId: string,
    itemId: string,
    role: string,
    date: string
  ): Promise<any | null> => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL ?? '';
    const url = `${apiUrl}/api/partnerRoleInfo/discount/${partnerId}/${itemId}?role=${role}&date=${date}`;

    const response = await fetch(url, {
      headers: { 'x-auth-token': token || '' }
    });

    if (!response.ok) return null;
    return await response.json();
  }, []);

  // Negotiated price/discounts for many items of one partner in one go (Excel import).
  // Chunked by 500 ids; throws on a non-ok response so the caller can show one toast.
  const fetchPartnerItemDiscountsBulk = useCallback(async (
    partnerId: string,
    role: string,
    date: string,
    itemIds: string[]
  ): Promise<Record<string, any>> => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL ?? '';
    const result: Record<string, any> = {};

    for (let i = 0; i < itemIds.length; i += 500) {
      const response = await fetch(`${apiUrl}/api/partnerRoleInfo/discounts/bulk`, {
        method: 'POST',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ partnerId, role, date, itemIds: itemIds.slice(i, i + 500) })
      });
      if (!response.ok) throw new Error('bulk discounts failed');
      const data = await response.json();
      Object.assign(result, data?.discounts || {});
    }
    return result;
  }, []);

  const fetchQtyPerPack = useCallback(async (
    itemId: string,
    partnerId: string | undefined,
    role: 'buyer' | 'supplier',
    transfer: boolean
  ): Promise<number | null> => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL ?? '';
    const params = new URLSearchParams({ item: itemId });
    if (partnerId && !transfer) {
      params.set('partner', partnerId);
      params.set('role', role);
    }
    if (transfer) params.set('transfer', 'true');

    const response = await fetch(`${apiUrl}/api/document-line-item/qty-per-pack?${params.toString()}`, {
      headers: { 'x-auth-token': token || '' }
    });

    if (!response.ok) return null;
    const data = await response.json();
    const qtyPerPack = Number(data?.qtyPerPack);
    return Number.isFinite(qtyPerPack) && qtyPerPack > 0 ? qtyPerPack : null;
  }, []);

  // Total stock quantity across warehouses for an item (or only warehouseCode when given). Returns
  // null on a non-ok response so callers only update the row when a value was actually resolved.
  const fetchItemTotalStock = useCallback(async (itemId: string, warehouseCode?: string): Promise<number | null> => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL ?? '';

    const response = await fetch(`${apiUrl}/api/stock-overview?itemId=${itemId}`, {
      headers: { 'x-auth-token': token || '' }
    });

    if (!response.ok) return null;
    const stockData = await response.json();
    if (!stockData.stockData || !Array.isArray(stockData.stockData)) return 0;
    return stockData.stockData
      .filter((item: any) => !warehouseCode || item.warehouseCode === warehouseCode)
      .reduce((sum: number, item: any) => sum + (item.totalQuantity || 0), 0);
  }, []);

  return {
    fetchItemsByIds,
    lookupItemsByIdents,
    fetchPartnerItemDiscount,
    fetchPartnerItemDiscountsBulk,
    fetchQtyPerPack,
    fetchItemTotalStock
  };
};

export default useDocumentLineLookups;
